import isEqual from 'lodash/isEqual'

import {requestAnimationFrameThrottle} from './dom'
import {
  Dimensions,
  Position,
  Rectangle,
  observeDimensions,
  observeDocumentRect,
  observeIntersection,
  observeScroll,
  observeWindowResize,
} from './dom-observer'

export type PositionPopoverHandler = (
  popoverDimensions: Dimensions,
  anchorRect: Rectangle,
  viewportRect: Rectangle
) => Position | null

export type CSSMarginShorthand =
  | number
  | [number, number]
  | [number, number, number]
  | [number, number, number, number]

export type PopoverState = 'hidden' | 'measure' | 'visible'

export const PopoverAnchorPlacement = {
  topStart: 'top-start',
  topCenter: 'top-center',
  topEnd: 'top-end',
  rightStart: 'right-start',
  rightCenter: 'right-center',
  rightEnd: 'right-end',
  bottomStart: 'bottom-start',
  bottomCenter: 'bottom-center',
  bottomEnd: 'bottom-end',
  leftStart: 'left-start',
  leftCenter: 'left-center',
  leftEnd: 'left-end',
} as const

export type PopoverAnchorPlacement =
  (typeof PopoverAnchorPlacement)[keyof typeof PopoverAnchorPlacement]

export const isInsideRect = (rect: Rectangle, parentRect: Rectangle) => {
  return (
    rect.x >= parentRect.x &&
    rect.y >= parentRect.y &&
    rect.x + rect.width <= parentRect.x + parentRect.width &&
    rect.y + rect.height <= parentRect.y + parentRect.height
  )
}

export const multiplyCSSMarginShorthand = (
  margin: CSSMarginShorthand,
  multiplier: number
): CSSMarginShorthand => {
  if (typeof margin === 'number') {
    return margin * multiplier
  }

  return margin.map(value => value * multiplier) as CSSMarginShorthand
}

export const getHorizontalDistanceToPreferredPosition = (
  position: Position,
  preferredPosition: Position
) => {
  return Math.abs(position.x - preferredPosition.x)
}

export const getVerticalDistanceToPreferredPosition = (
  position: Position,
  preferredPosition: Position
) => {
  return Math.abs(position.y - preferredPosition.y)
}

export const getDistanceToPreferredPosition = (position: Position, preferredPosition: Position) => {
  return (
    getHorizontalDistanceToPreferredPosition(position, preferredPosition) +
    getVerticalDistanceToPreferredPosition(position, preferredPosition)
  )
}

/**
 * Shrinks or expands the rect to all axes by the given padding.
 * This means this also works with negative padding.
 * Uses the same logic as CSS padding for directions which means you can either pass a single number
 * which will be used for all directions, two numbers which will be used for top/bottom and left/right,
 * three numbers which will be used for top, left/right and bottom or four numbers which will be used for
 * top, right, bottom and left.
 */
export function addMargin(rect: Rectangle, padding: CSSMarginShorthand): Rectangle {
  let resolvedPadding: [number, number, number, number]

  if (typeof padding === 'number') {
    resolvedPadding = [padding, padding, padding, padding]
  } else if (padding.length === 2) {
    resolvedPadding = [padding[0], padding[1], padding[0], padding[1]]
  } else if (padding.length === 3) {
    resolvedPadding = [padding[0], padding[1], padding[2], padding[1]]
  } else {
    resolvedPadding = padding
  }

  return {
    x: rect.x - resolvedPadding[3],
    y: rect.y - resolvedPadding[0],
    width: rect.width + resolvedPadding[1] + resolvedPadding[3],
    height: rect.height + resolvedPadding[0] + resolvedPadding[2],
  }
}

export function restrictToRect(rect: Rectangle, parentRect: Rectangle): Rectangle {
  const nextRect: Rectangle = {...rect}

  if (rect.x < parentRect.x) {
    nextRect.x = parentRect.x
  } else if (rect.x + rect.width > parentRect.x + parentRect.width) {
    nextRect.x = parentRect.x + parentRect.width - rect.width
  }

  if (rect.y < parentRect.y) {
    nextRect.y = parentRect.y
  } else if (rect.y + rect.height > parentRect.y + parentRect.height) {
    nextRect.y = parentRect.y + parentRect.height - rect.height
  }

  return nextRect
}

export function placeAroundRect(
  rect: Rectangle,
  anchorRect: Rectangle,
  placement: PopoverAnchorPlacement = PopoverAnchorPlacement.bottomStart,
  distance = 0,
  shift = 0 // Perpendicular distance from anchor.
): Rectangle {
  switch (placement) {
    case PopoverAnchorPlacement.topStart:
      return {
        ...rect,
        x: anchorRect.x + shift,
        y: anchorRect.y - rect.height - distance,
      }
    case PopoverAnchorPlacement.topCenter:
      return {
        ...rect,
        x: anchorRect.x + 0.5 * (anchorRect.width - rect.width) + shift,
        y: anchorRect.y - rect.height - distance,
      }
    case PopoverAnchorPlacement.topEnd:
      return {
        ...rect,
        x: anchorRect.x + anchorRect.width - rect.width - shift,
        y: anchorRect.y - rect.height - distance,
      }
    case PopoverAnchorPlacement.bottomStart:
      return {
        ...rect,
        x: anchorRect.x + shift,
        y: anchorRect.y + anchorRect.height + distance,
      }
    case PopoverAnchorPlacement.bottomCenter:
      return {
        ...rect,
        x: anchorRect.x + 0.5 * (anchorRect.width - rect.width) + shift,
        y: anchorRect.y + anchorRect.height + distance,
      }
    case PopoverAnchorPlacement.bottomEnd:
      return {
        ...rect,
        x: anchorRect.x + anchorRect.width - rect.width - shift,
        y: anchorRect.y + anchorRect.height + distance,
      }
    case PopoverAnchorPlacement.leftStart:
      return {
        ...rect,
        x: anchorRect.x - rect.width - distance,
        y: anchorRect.y + shift,
      }
    case PopoverAnchorPlacement.leftCenter:
      return {
        ...rect,
        x: anchorRect.x - rect.width - distance,
        y: anchorRect.y + 0.5 * (anchorRect.height - rect.height) + shift,
      }
    case PopoverAnchorPlacement.leftEnd:
      return {
        ...rect,
        x: anchorRect.x - rect.width - distance,
        y: anchorRect.y + anchorRect.height - rect.height - shift,
      }
    case PopoverAnchorPlacement.rightStart:
      return {
        ...rect,
        x: anchorRect.x + anchorRect.width + distance,
        y: anchorRect.y + shift,
      }
    case PopoverAnchorPlacement.rightCenter:
      return {
        ...rect,
        x: anchorRect.x + anchorRect.width + distance,
        y: anchorRect.y + 0.5 * (anchorRect.height - rect.height) + shift,
      }
    case PopoverAnchorPlacement.rightEnd:
    default:
      return {
        ...rect,
        x: anchorRect.x + anchorRect.width + distance,
        y: anchorRect.y + anchorRect.height - rect.height - shift,
      }
  }
}

export const defaultPlacementFallbacks: Record<PopoverAnchorPlacement, PopoverAnchorPlacement[]> = {
  [PopoverAnchorPlacement.topStart]: [PopoverAnchorPlacement.bottomStart],
  [PopoverAnchorPlacement.topEnd]: [PopoverAnchorPlacement.bottomEnd],
  [PopoverAnchorPlacement.topCenter]: [PopoverAnchorPlacement.bottomCenter],
  [PopoverAnchorPlacement.bottomStart]: [PopoverAnchorPlacement.topStart],
  [PopoverAnchorPlacement.bottomEnd]: [PopoverAnchorPlacement.topEnd],
  [PopoverAnchorPlacement.bottomCenter]: [PopoverAnchorPlacement.topCenter],
  [PopoverAnchorPlacement.leftStart]: [PopoverAnchorPlacement.rightStart],
  [PopoverAnchorPlacement.leftEnd]: [PopoverAnchorPlacement.rightEnd],
  [PopoverAnchorPlacement.leftCenter]: [PopoverAnchorPlacement.rightCenter],
  [PopoverAnchorPlacement.rightStart]: [PopoverAnchorPlacement.leftStart],
  [PopoverAnchorPlacement.rightEnd]: [PopoverAnchorPlacement.leftEnd],
  [PopoverAnchorPlacement.rightCenter]: [PopoverAnchorPlacement.leftCenter],
}

export const positionPopover =
  (
    placement: PopoverAnchorPlacement | PopoverAnchorPlacement[] = [
      PopoverAnchorPlacement.bottomStart,
      PopoverAnchorPlacement.topStart,
    ],
    distanceToAnchor = 0,
    shiftFromAnchor = 0, // Perpendicular distance from anchor.
    viewportMargin = 10
  ): PositionPopoverHandler =>
  (popoverDimensions, anchorRect, viewportRect) => {
    const placements = Array.isArray(placement) ? placement : [placement]
    const popoverRect = {
      ...popoverDimensions,
      x: 0,
      y: 0,
    }
    const viewportRectWithMargin = addMargin(viewportRect, -1 * viewportMargin)

    let minDistance = Number.MAX_VALUE
    let minPosition: Position | null = null

    // Search for placement with minimum distance to preferred position.
    // eslint-disable-next-line no-restricted-syntax
    for (const pl of placements) {
      const placedRect = placeAroundRect(
        popoverRect,
        anchorRect,
        pl,
        distanceToAnchor,
        shiftFromAnchor
      )
      const restrictedRect = restrictToRect(placedRect, viewportRectWithMargin)

      const popoverPosition: Position = {
        x: restrictedRect.x,
        y: restrictedRect.y,
      }
      const preferredPosition: Position = {
        x: placedRect.x,
        y: placedRect.y,
      }

      const distance = getDistanceToPreferredPosition(popoverPosition, preferredPosition)

      // Abort if we already found optimal distance.
      if (distance === 0) {
        return popoverPosition
      }

      if (distance < minDistance) {
        minDistance = distance
        minPosition = popoverPosition
      }
    }

    return minPosition
  }

export const observePositionPopover = (
  popoverElement: HTMLElement,
  anchorElement: HTMLElement,
  callback: (
    anchorRect: Rectangle | null,
    popoverRect: Rectangle | null,
    popoverState: PopoverState
  ) => void,
  positionHandler: PositionPopoverHandler
) => {
  const cleanups: Array<() => void> = []

  let anchorRect: Rectangle | null = null
  let popoverDimensions: Dimensions | null = null
  let isAnchorVisible: boolean = false
  let isMeasuring: boolean = false

  const update = () => {
    if (!anchorRect || !popoverDimensions || !isAnchorVisible) {
      callback(anchorRect, null, 'hidden')

      return {
        anchorRect,
        popoverPosition: null,
      }
    }

    const viewportRect = {
      x: window.scrollX,
      y: window.scrollY,
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
    }

    // Render invisible but within viewport to measure dimensions.
    if (!isMeasuring && popoverDimensions.width + popoverDimensions.height === 0) {
      isMeasuring = true

      callback(anchorRect, null, 'measure')

      return {
        anchorRect,
        popoverPosition: null,
      }
    }

    const popoverPosition = positionHandler(popoverDimensions, anchorRect, viewportRect)

    const popoverRect = popoverPosition
      ? {
          ...popoverDimensions,
          ...popoverPosition,
        }
      : null

    // Hide element shortly after measuring to retrigger potential starting style animations with final position.
    if (isMeasuring && popoverPosition) {
      callback(anchorRect, popoverRect, 'hidden')
      return undefined
    }

    callback(anchorRect, popoverRect, popoverPosition ? 'visible' : 'hidden')

    return {
      anchorRect,
      popoverPosition,
    }
  }

  const throttledUpdate = requestAnimationFrameThrottle(update)

  cleanups.push(
    observeDocumentRect(anchorElement, value => {
      if (!isEqual(value, anchorRect)) {
        anchorRect = value
        throttledUpdate()
      }
    })
  )

  cleanups.push(
    observeDimensions(popoverElement, value => {
      if (!isEqual(value, popoverDimensions)) {
        // After measuring the popover is shortly hidden again which will trigger this observer.
        // Here we end the measuring state and finally show the popover.
        if (isMeasuring && value.width + value.height === 0) {
          isMeasuring = false
        } else {
          popoverDimensions = value
        }
        throttledUpdate()
      }
    })
  )

  cleanups.push(
    observeIntersection(anchorElement, value => {
      const updatedValue = value.isIntersecting
      if (updatedValue !== isAnchorVisible) {
        isAnchorVisible = updatedValue
        throttledUpdate()
      }
    })
  )

  cleanups.push(observeScroll(window, throttledUpdate))

  cleanups.push(observeWindowResize(throttledUpdate))

  throttledUpdate()

  return () => {
    cleanups.forEach(cancel => cancel())
  }
}
